import { useEffect } from 'react';

import { ToastType } from '@global-stores/toast/types';

const TOAST_STATE = {
  success: {
    icon: `fa fa-check`,
    color: `#38a169`,
  },
  error: {
    icon: `fa fa-times-circle `,
    color: `#e53e3e`,
  },
  warning: {
    icon: `fa fa-exclamation-triangle`,
    color: `#dd6b20`,
  },
};

interface ToastProps {
  isOpen: boolean;
  title: string;
  message: string;
  type?: ToastType;
  align?: 'bottom' | 'top';
  autoClose?: boolean;
  onClose: () => void;
}

export const Toast = ({
  isOpen,
  title,
  message,
  type = `success`,
  align = `top`,
  autoClose = false,
  onClose,
}: ToastProps) => {
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isOpen && autoClose) {
      timer = setTimeout(() => {
        onClose();
      }, 9000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isOpen]);

  return (
    <div className={`toast__wrapper ${align}`}>
      <div className={`toast ${isOpen ? `show` : ``}`}>
        <div className="toast__box">
          <i className={`toast__icon ${TOAST_STATE[type].icon}`} />
          <h4>{title}</h4>
          <p>{message}</p>
          <button type="button" className="toast__close-button" onClick={onClose}>
            <i className="fa fa-times" />
          </button>
        </div>
      </div>
      <style jsx>
        {`
          .toast__wrapper {
            position: fixed;
            max-width: 600px;
            width: 100%;
            display: flex;
            justify-content: center;
            overflow: hidden;
          }

          .toast__wrapper.top {
            top: 0;
            right: 0;
            margin-top: 10px;
            justify-content: flex-end;
          }

          .toast__wrapper.bottom {
            bottom: 0;
            right: 50%;
            transform: translateX(50%);
          }

          .toast {
            display: none;
            max-width: 500px;
            width: 100%;
            height: 100%;
          }

          .toast.show {
            display: inline-block;
          }

          .toast__wrapper.top .toast {
            margin-right: -100%;
          }

          .toast__wrapper.bottom .toast {
            margin-bottom: -100%;
          }

          .toast__wrapper.top .toast.show {
            margin-right: 10px;
            animation: slideFromRight 0.5s;
          }

          .toast__wrapper.bottom .toast.show {
            bottom: 1%;
            margin-bottom: 120px;
            animation: slideFromBottom 0.5s;
          }

          .toast__box {
            background-color: white;
            border-radius: 6px;
            box-shadow: rgba(14, 18, 22, 0.35) 0px 10px 38px -10px,
              rgba(14, 18, 22, 0.2) 0px 10px 20px -15px;
            grid-template-areas: 'icon title action' 'icon description action';
            grid-template-columns: max-content 1fr 0.1fr;
            padding: 15px;
            display: grid;
            column-gap: 15px;
            align-items: center;
          }

          .toast__icon {
            grid-area: icon;
            color: ${TOAST_STATE[type].color};
            justify-self: center;
            font-size: 20px;
          }

          .toast__box h4 {
            grid-area: title;
            margin-bottom: 10px;
            font-weight: 500;
            color: rgb(17, 24, 28);
            font-size: 18px;
          }

          .toast__box p {
            grid-area: description;
            line-height: 1.4;
            max-width: 400px;
            justify-self: flex-start;
          }

          .toast__close-button {
            grid-area: action;
            background: transparent;
            align-self: flex-start;
            justify-self: flex-end;
            margin-top: 10px;
          }

          @keyframes slideFromBottom {
            0% {
              margin-bottom: -100%;
            }
            100% {
              margin-bottom: 120px;
            }
          }

          @keyframes slideFromRight {
            0% {
              margin-right: -100%;
            }
            100% {
              margin-right: 10px;
            }
          }
        `}
      </style>
    </div>
  );
};
